import { useDisclosure } from '@chakra-ui/hooks';
import DeleteModal from 'components/actions/Delete';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import PaymentScheduleEditor from 'components/fields/PaymentScheduleEditor';
import {
    decodeLegacyDepositPreset,
    DEFAULT_CUSTOM_SCHEDULE,
    CUSTOM_SCHEDULE_OPTION,
} from 'utils/paymentSchedule';
import {
    archiveCustomer,
    checkCustomerCode,
    getCustomer,
    getCustomerAcquisitionYears,
    updateCustomer,
    updateCustomerBlocked,
    updateCustomerRestricted,
} from 'interfaces/customer';
import { getDeliveryTours } from 'interfaces/deliveryTour';
import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import {
    isFormErrored,
    isValidEmail,
    isValidPhoneNumber,
} from 'utils/verification';
import SaveStatus from 'components/actions/SaveStatus';
import GoBack from 'components/actions/GoBack';
import InputTagField from 'components/fields/InputTagField';
import { getCompanyAccounts, getMyInfo, updateMyInfo } from 'interfaces/user';
import Switch from 'components/switch';
import { Archive, Building2, CheckCircle2, Landmark, Receipt, Truck } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const STATUT_META: Record<string, { label: string; badge: string; dot: string }> = {
    actif: {
        label: 'Client actif',
        badge: 'bg-green-100 text-green-800 dark:bg-green-400/10 dark:text-green-400',
        dot: 'bg-green-500',
    },
    prospect: {
        label: 'Prospect',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-400/10 dark:text-blue-400',
        dot: 'bg-blue-500',
    },
    perdu: {
        label: 'Perdu',
        badge: 'bg-red-100 text-red-800 dark:bg-red-400/10 dark:text-red-400',
        dot: 'bg-red-500',
    },
    liquidation: {
        label: 'Liquidation',
        badge: 'bg-orange-100 text-orange-800 dark:bg-orange-400/10 dark:text-orange-400',
        dot: 'bg-orange-500',
    },
    autre: {
        label: 'Autre',
        badge: 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300',
        dot: 'bg-gray-400',
    },
};

const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
};

const StatusBadge = ({ statut }: { statut: string }) => {
    const meta = STATUT_META[statut] || STATUT_META.actif;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${meta.badge}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
};

const SectionHeader = ({
    icon: Icon,
    title,
    description,
}: {
    icon: any;
    title: string;
    description?: string;
}) => (
    <div className="mb-5 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-toola-500/10 text-toola-600 dark:bg-toola-400/10 dark:text-toola-400">
            <Icon size={18} strokeWidth={2} />
        </div>
        <div>
            <h5 className="text-sm font-semibold text-navy-700 dark:text-white">
                {title}
            </h5>
            {description && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {description}
                </p>
            )}
        </div>
    </div>
);

const FieldGroupLabel = ({ children }: { children: any }) => (
    <div className="col-span-12 mt-6 first:mt-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {children}
        </p>
    </div>
);

const ToggleCard = ({
    id,
    label,
    checked,
    onChange,
    disabled,
}: {
    id: string;
    label: string;
    checked: boolean;
    onChange: () => void;
    disabled?: boolean;
}) => (
    <label
        htmlFor={id}
        className={`flex h-full items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${
            checked
                ? 'border-toola-500/40 bg-toola-500/5'
                : 'border-gray-200 dark:border-white/10'
        }`}
    >
        <span className="pr-2 text-xs font-medium text-navy-700 dark:text-white">
            {label}
        </span>
        <Switch onChange={onChange} id={id} isDefaultChecked={checked} disabled={disabled} color="toola" />
    </label>
);

const EditInformationCustomer = () => {
    const navigate = useNavigate();
    const params = useParams();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [customer, setCustomer] = useState<any>(null);
    const [deliveryTours, setDeliveryTours] = useState<any>([]);
    const [isBillingAddress, setIsBillingAddress] = useState(false);
    const [saveStatus, setSaveStatus] = useState<any>('none');
    const [user, setUser] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [customerCodeAvailable, setCustomerCodeAvailable] = useState<boolean | null>(null);
    const [customerCodeChecking, setCustomerCodeChecking] = useState(false);
    const [acquisitionYears, setAcquisitionYears] = useState<string[]>([]);
    const [showBlockReasonInput, setShowBlockReasonInput] = useState(false);
    const [blockReasonDraft, setBlockReasonDraft] = useState('');
    const [blockActionPending, setBlockActionPending] = useState(false);
    const [restrictActionPending, setRestrictActionPending] = useState(false);

    const currentUserId = localStorage.getItem('userId') || '';
    const isAccountOwner = !localStorage.getItem('ownerId');

    const canBlockCustomer = useMemo(() => {
        const storedPermissions = JSON.parse(
            localStorage.getItem('permissions') || '[]',
        ) as string[];
        const isOwner = !localStorage.getItem('ownerId');
        return (
            isOwner ||
            storedPermissions.includes('all') ||
            storedPermissions.includes('block:customers')
        );
    }, []);

    // Only a manager holding restrict:customers (or the owner) can lock a
    // customer to themselves + the owner — see restrict:customers permission
    // and PUT /customers/:id/restrict.
    const canRestrictCustomer = useMemo(() => {
        const storedPermissions = JSON.parse(
            localStorage.getItem('permissions') || '[]',
        ) as string[];
        return (
            isAccountOwner ||
            storedPermissions.includes('all') ||
            storedPermissions.includes('restrict:customers')
        );
    }, [isAccountOwner]);

    const restrictedByMe = !!customer?.restrictedToUserId && customer.restrictedToUserId === currentUserId;
    // The owner can always see/unlock any lock; for anyone else, a lock held
    // by someone other than themselves is read-only (mirrors the backend's
    // 403 in PUT /customers/:id/restrict).
    const restrictedByOther =
        !!customer?.restrictedToUserId && !isAccountOwner && customer.restrictedToUserId !== currentUserId;

    const initUser = async () => {
            const res = await getMyInfo();
            if (res.data) {
                setUser(res.data);
            }
        }

    const initAcquisitionYears = async () => {
        const res = await getCustomerAcquisitionYears();
        if (res) setAcquisitionYears(res);
    };
    

    const addNewTag = async (newTag: string) => {
        if (!user) return;
        await updateMyInfo({ tags: [...user.tags, newTag] });
    };

    const addTransformationType = async (newType: string) => {
        if (!user) return;
        const updated = [...(user.transformationTypes || []), newType];
        await updateMyInfo({ transformationTypes: updated });
        setUser((prev: any) => ({ ...prev, transformationTypes: updated }));
    };

    const addMemberType = async (newType: string) => {
        if (!user) return;
        const updated = [...(user.memberTypes || []), newType];
        await updateMyInfo({ memberTypes: updated });
        setUser((prev: any) => ({ ...prev, memberTypes: updated }));
    };

    const addClientTarget = async (newTarget: string) => {
        if (!user) return;
        const updated = [...(user.clientTargets || []), newTarget];
        await updateMyInfo({ clientTargets: updated });
        setUser((prev: any) => ({ ...prev, clientTargets: updated }));
    };

    const addClientSpecificity = async (newSpecificity: string) => {
        if (!user) return;
        const updated = [...(user.clientSpecificities || []), newSpecificity];
        await updateMyInfo({ clientSpecificities: updated });
        setUser((prev: any) => ({ ...prev, clientSpecificities: updated }));
    };

    const handleChange = (e: any) => {
        setSaveStatus('saving');
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const handleChangeBilling = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            billingAddress: e.target.value,
            deliveryAddress: e.target.value,
        }));
    };

    const handleChangeDeliveryCity = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            deliveryCity: e.target.value,
            billingCity: e.target.value,
        }));
    };

    const handleChangeDeliveryZip = (e: any) => {
        if (isBillingAddress) {
            handleChange(e);
            return;
        }
        setCustomer((prevCustomer: any) => ({
            ...prevCustomer,
            deliveryZipCode: e.target.value,
            billingZipCode: e.target.value,
        }));
    };

    const handleToggleBlocked = async () => {
        if (!canBlockCustomer || !customer || blockActionPending) return;
        if (customer.isBlocked) {
            setBlockActionPending(true);
            const res = await updateCustomerBlocked({
                _id: customer._id,
                isBlocked: false,
            });
            if (res.data) setCustomer((prev: any) => ({ ...prev, ...res.data }));
            setBlockActionPending(false);
        } else {
            setShowBlockReasonInput(true);
        }
    };

    const handleToggleRestricted = async () => {
        if (!canRestrictCustomer || !customer || restrictActionPending || restrictedByOther) return;
        setRestrictActionPending(true);
        const res = await updateCustomerRestricted(customer._id, !restrictedByMe);
        if (res.data) {
            setCustomer((prev: any) => ({ ...prev, restrictedToUserId: res.data.restrictedToUserId }));
        }
        setRestrictActionPending(false);
    };

    const confirmBlockCustomer = async () => {
        setBlockActionPending(true);
        const res = await updateCustomerBlocked({
            _id: customer._id,
            isBlocked: true,
            blockedReason: blockReasonDraft || undefined,
        });
        if (res.data) {
            setCustomer((prev: any) => ({ ...prev, ...res.data }));
            setShowBlockReasonInput(false);
            setBlockReasonDraft('');
        }
        setBlockActionPending(false);
    };

    const initCustomer = async (id: string) => {
        const res = await getCustomer(id);
        if (res) {
            if (res.billingAddress) setIsBillingAddress(true);
            setCustomer(res);
            return;
        }
        navigate('/customers');
    };

    const initDeliveryTours = async () => {
        const res = await getDeliveryTours(false);
        if (res) {
            setDeliveryTours(res.data);
            return;
        }
    };

    const initAccounts = async () => {
        const res = await getCompanyAccounts();
        if (res) {
            setAccounts(res.data);
        }
    };

    const editCustomer = async () => {
        if (!customer) return;

        const validationRules: any = {
            mail: isValidEmail,
            billingEmail: isValidEmail,
            phone: (value: any) => !value || isValidPhoneNumber(value),
            //vatNumber: (value: any) => !value || isValidVATNumber(value),
            //siren: (value: any) => !value || isValidSiren(value),
            company: (value: any) => !!value.trim(),
        };

        if (isFormErrored(customer, validationRules)) {
            setSaveStatus('error');
            return;
        }

        const data = {
            _id: customer._id,
            deliveryTourId: customer.deliveryTourId,
            deliveryAddress: customer.deliveryAddress,
            deliveryCity: customer.deliveryCity,
            deliveryZipCode: customer.deliveryZipCode,
            francoPort: customer.francoPort,
            billingAddress: customer.billingAddress,
            billingCity: customer.billingCity,
            billingZipCode: customer.billingZipCode,
            billingEmail: customer.billingEmail,
            secondBillingEmail: customer.secondBillingEmail,
            billingPeriod: customer.billingPeriod,
            contact: customer.contact,
            contactPoste: customer.contactPoste,
            website: customer.website,
            company: customer.company,
            customerId: customer.customerId,
            customerCode: customer.customerCode,
            mail: customer.mail,
            notes: customer.notes,
            deliveryNotes: customer.deliveryNotes,
            vatNumber: customer.vatNumber,
            siren: customer.siren,
            phone: customer.phone,
            phone2: customer.phone2,
            paymentMethod: customer.paymentMethod,
            statut: customer.statut,
            raisonPerte: customer.raisonPerte,
            typeTransformation: customer.typeTransformation,
            typeMembre: customer.typeMembre,
            cibleClient: customer.cibleClient,
            specificiteClient: customer.specificiteClient,
            anneeAcquisition: customer.anneeAcquisition,
            displayName: customer.displayName,
            hidePricesOnShop: customer.hidePricesOnShop,
            cotisationInterfel: customer.cotisationInterfel,
            sendNewsletter: customer.sendNewsletter,
            tags: customer.tags,
            assignedTo: customer.assignedTo,
            sendDeliveryEmail: customer.sendDeliveryEmail,
            deliveryNoteType: customer.deliveryNoteType,
            isHT: customer.isHT,
            invoiceDueDelay: customer.invoiceDueDelay,
            paymentSchedule: customer.paymentSchedule,
            useSecondRib: customer.useSecondRib,
            deliveryEmail: customer.deliveryEmail,
            customerRib: customer.customerRib,
            herRib: customer.herRib,
            mandateReference: customer.mandateReference,
            mandateSignatureDate: customer.mandateSignatureDate,
            bankName: customer.bankName,
            bankCode: customer.bankCode,
            bankBranchCode: customer.bankBranchCode,
            bankAccountNumber: customer.bankAccountNumber,
            bankRibKey: customer.bankRibKey,
            bankBic: customer.bankBic,
            additionalMails: customer.additionalMails,
            invoiceMessage: customer.invoiceMessage,
            creancePossible: customer.creancePossible,
        };
        const res = await updateCustomer(data);
        if (res.data) setSaveStatus('saved');
        else setSaveStatus('error');
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            editCustomer();
        }, 2000);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer]);

    useEffect(() => {
        const code = customer?.customerCode;
        if (!code) {
            setCustomerCodeAvailable(null);
            return;
        }
        setCustomerCodeChecking(true);
        const timer = setTimeout(async () => {
            const available = await checkCustomerCode(code, customer?._id);
            setCustomerCodeAvailable(available);
            setCustomerCodeChecking(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [customer?.customerCode]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initCustomer(params.id);
        initDeliveryTours();
        initUser();
        initAccounts();
        initAcquisitionYears();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const assignedAccountName = accounts.find((a: any) =>
        customer?.assignedTo?.includes(a._id),
    )?.fullName;

    const blockedByName = customer?.blockedBy
        ? accounts.find((a: any) => a._id === customer.blockedBy)?.fullName ||
          ((user as any)?._id === customer.blockedBy
              ? (user as any)?.fullName || 'vous'
              : 'un utilisateur')
        : null;

    return (
        <>
            <div className="mt-1 col-span-12">
                <GoBack text="Fiche client" />
            </div>
            <div className="mt-2 grid grid-cols-12 gap-4">
                <DeleteModal
                    isOpen={isOpen}
                    onClose={onClose}
                    description="Êtes-vous sûr de vouloir archiver ce client ?"
                    context="un client"
                    archived={true}
                    deleteFunction={() => {
                        archiveCustomer(params.id);
                        navigate('/customers');
                    }}
                />

                {/* Header */}
                <div className="col-span-12">
                    <Card extra="w-full !p-5 md:!p-6 border">
                        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div className="flex min-w-0 items-center gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-toola-500 to-toola-700 text-sm font-bold text-white">
                                    {getInitials(customer?.company)}
                                </div>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h4 className="truncate text-lg font-semibold text-navy-700 dark:text-white">
                                            {customer
                                                ? customer.company ||
                                                  'Client sans nom'
                                                : 'Édition client'}
                                        </h4>
                                        {customer && (
                                            <StatusBadge
                                                statut={
                                                    customer.statut || 'actif'
                                                }
                                            />
                                        )}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                        {customer?.customerId && (
                                            <span>
                                                N° {customer.customerId}
                                            </span>
                                        )}
                                        {customer?.customerCode && (
                                            <span>
                                                · Code {customer.customerCode}
                                            </span>
                                        )}
                                        {assignedAccountName && (
                                            <span>
                                                · Commercial :{' '}
                                                {assignedAccountName}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-3 self-start md:self-center">
                                <SaveStatus status={saveStatus} />
                                <button
                                    className="linear flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-xl border border-red-500 px-3 py-2 text-xs font-medium text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={() => onOpen()}
                                >
                                    <Archive size={14} />
                                    Archiver
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Informations client */}
                <div className="col-span-12">
                    <Card extra="w-full !p-5 md:!p-6 border">
                        <SectionHeader
                            icon={Building2}
                            title="Informations client"
                            description="Identité, contact et suivi commercial"
                        />
                        <div className="grid gap-4 grid-cols-12">
                            <FieldGroupLabel>Identité</FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label="Numéro client (compta)"
                                    placeholder="00012"
                                    value={customer?.customerId}
                                    id="customerId"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Code client${customerCodeChecking ? ' …' : customerCodeAvailable === false ? ' ✗ déjà utilisé' : customerCodeAvailable === true ? ' ✓' : ''}`}
                                    placeholder="CLI-001"
                                    value={customer?.customerCode || ''}
                                    id="customerCode"
                                    type="text"
                                    onChange={handleChange}
                                    error="Code client déjà utilisé"
                                    isInvalid={customerCodeAvailable === false}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Nom commercial"
                                    placeholder="Nom commercial"
                                    value={
                                        customer?.displayName
                                            ? customer?.displayName
                                            : ''
                                    }
                                    id="displayName"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Raison sociale*"
                                    placeholder="Toola"
                                    value={customer?.company}
                                    id="company"
                                    type="text"
                                    onChange={handleChange}
                                    error="Raison sociale invalide"
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <FieldGroupLabel>Contact</FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Email principal (Boutique B2B)*"
                                    placeholder="mathieu@toola.eu"
                                    error={'Champ requis'}
                                    isInvalid={!isValidEmail(customer?.mail)}
                                    value={customer?.mail || ''}
                                    id="mail"
                                    type="email"
                                    onChange={handleChange}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputTagField
                                    label="Emails supplémentaires (boutique B2B)"
                                    placeholder="Ajouter un email puis Entrée"
                                    id="additionalMails"
                                    type="text"
                                    extra=""
                                    tags={customer?.additionalMails}
                                    allTags={[]}
                                    onTagCreate={() => {}}
                                    onChange={(tags: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prev: any) => ({ ...prev, additionalMails: tags }));
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label="Téléphone"
                                    placeholder="Téléphone"
                                    error={'invalide'}
                                    isInvalid={
                                        !isValidPhoneNumber(customer?.phone)
                                    }
                                    value={customer?.phone}
                                    id="phone"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label="Téléphone 2"
                                    placeholder="Téléphone 2"
                                    error={'invalide'}
                                    isInvalid={
                                        customer?.phone2 && !isValidPhoneNumber(customer?.phone2)
                                    }
                                    value={customer?.phone2}
                                    id="phone2"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Contact`}
                                    placeholder="Contact"
                                    value={customer?.contact}
                                    id="contact"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <InputField
                                    label={`Poste du contact`}
                                    placeholder="Directeur achats"
                                    value={customer?.contactPoste || ''}
                                    id="contactPoste"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Site web`}
                                    placeholder="https://www.client.fr"
                                    value={customer?.website || ''}
                                    id="website"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-8 grid">
                                <InputField
                                    label={`Notes utiles`}
                                    placeholder="Notes utiles"
                                    value={customer?.notes}
                                    id="notes"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>

                            <FieldGroupLabel>
                                Suivi commercial
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-6">
                                <InputTagField
                                    label="Tags clients"
                                    placeholder=""
                                    id="tags"
                                    type="text"
                                    extra=""
                                    tags={customer?.tags}
                                    allTags={
                                        user
                                            ? user.tags.map((name: string) => ({
                                                  name,
                                              }))
                                            : []
                                    }
                                    onTagCreate={(tag: any) => addNewTag(tag)}
                                    onChange={(tags: any) =>
                                        handleChange({
                                            target: { id: 'tags', value: tags },
                                        })
                                    }
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Commercial assigné"
                                    displayKey="fullName"
                                    options={accounts}
                                    value={
                                        customer?.assignedTo?.length > 0
                                            ? customer.assignedTo[0]
                                            : null
                                    }
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        handleChange({
                                            target: {
                                                id: 'assignedTo',
                                                value: [e._id],
                                            },
                                        });
                                    }}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Statut"
                                    displayKey="name"
                                    options={[
                                        { _id: 'actif', name: 'Client actif' },
                                        { _id: 'prospect', name: 'Prospect' },
                                        { _id: 'perdu', name: 'Perdu' },
                                        { _id: 'liquidation', name: 'Liquidation' },
                                        { _id: 'autre', name: 'Autre' },
                                    ]}
                                    value={customer?.statut || 'actif'}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            statut: e._id,
                                        }));
                                    }}
                                    inputSmall={true}
                                />
                            </div>
                            <AnimatePresence>
                                {customer?.statut === 'perdu' && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.15 }}
                                        className="col-span-12 lg:col-span-3"
                                    >
                                        <InputField
                                            label="Raison de la perte"
                                            placeholder="Ex: parti chez un concurrent"
                                            value={customer?.raisonPerte || ''}
                                            id="raisonPerte"
                                            type="text"
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Type de transformation commerciale"
                                    displayKey="name"
                                    options={(user?.transformationTypes || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.typeTransformation}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            typeTransformation: e._id,
                                        }));
                                    }}
                                    onCreateOption={addTransformationType}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Type de membre"
                                    displayKey="name"
                                    options={(user?.memberTypes || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.typeMembre}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            typeMembre: e._id,
                                        }));
                                    }}
                                    onCreateOption={addMemberType}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Cible client"
                                    displayKey="name"
                                    options={(user?.clientTargets || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.cibleClient}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            cibleClient: e._id,
                                        }));
                                    }}
                                    onCreateOption={addClientTarget}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Spécificité client"
                                    displayKey="name"
                                    options={(user?.clientSpecificities || []).map(
                                        (t: string) => ({ _id: t, name: t }),
                                    )}
                                    value={customer?.specificiteClient}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            specificiteClient: e._id,
                                        }));
                                    }}
                                    onCreateOption={addClientSpecificity}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-2">
                                <SearchSelector
                                    label="Année d'acquisition"
                                    displayKey="name"
                                    options={acquisitionYears.map((y: string) => ({ _id: y, name: y }))}
                                    value={customer?.anneeAcquisition}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            anneeAcquisition: e._id,
                                        }));
                                    }}
                                    onCreateOption={(newYear: string) =>
                                        setAcquisitionYears((prev) => [...prev, newYear])
                                    }
                                    inputSmall={true}
                                />
                            </div>

                            <FieldGroupLabel>Préférences</FieldGroupLabel>
                            <div className="col-span-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                                <ToggleCard
                                    id="isHT"
                                    label="Client franchise en base TVA (sans TVA)"
                                    checked={!!customer?.isHT}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            isHT: !prevCustomer.isHT,
                                        }));
                                    }}
                                />
                                <ToggleCard
                                    id="hidePricesOnShop"
                                    label="Cacher prix dans boutique B2B"
                                    checked={!!customer?.hidePricesOnShop}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            hidePricesOnShop:
                                                !prevCustomer.hidePricesOnShop,
                                        }));
                                    }}
                                />
                                <ToggleCard
                                    id="cotisationInterfel"
                                    label="Cotisation interfel"
                                    checked={!!customer?.cotisationInterfel}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            cotisationInterfel:
                                                !prevCustomer.cotisationInterfel,
                                        }));
                                    }}
                                />
                                <ToggleCard
                                    id="sendNewsletter"
                                    label="Envoi newsletter"
                                    checked={!!customer?.sendNewsletter}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            sendNewsletter:
                                                !prevCustomer.sendNewsletter,
                                        }));
                                    }}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Informations de livraison */}
                <div className="col-span-12">
                    <Card extra="w-full !p-5 md:!p-6 border">
                        <SectionHeader
                            icon={Truck}
                            title="Livraison"
                            description="Adresse, tournée et bon de livraison"
                        />
                        <div className="grid gap-4 grid-cols-12">
                            <FieldGroupLabel>
                                Adresse de livraison
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Adresse de livraison*"
                                    placeholder={`Champ de Mars, 5 Av. Anatole France`}
                                    value={customer?.deliveryAddress || ''}
                                    id="deliveryAddress"
                                    type="text"
                                    onChange={handleChangeBilling}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Code postal"
                                    placeholder="75007"
                                    value={customer?.deliveryZipCode || ''}
                                    id="deliveryZipCode"
                                    type="text"
                                    onChange={handleChangeDeliveryZip}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Ville"
                                    placeholder="Paris"
                                    value={customer?.deliveryCity || ''}
                                    id="deliveryCity"
                                    type="text"
                                    onChange={handleChangeDeliveryCity}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <SearchSelector
                                    label="Tournée de livraison"
                                    displayKey="name"
                                    options={deliveryTours}
                                    value={customer?.deliveryTourId}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            deliveryTourId: e._id,
                                        }));
                                    }}
                                    canCreate={true}
                                    createNew={() =>
                                        window.open(
                                            '/settings/deliveries',
                                            '_blank',
                                        )
                                    }
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Franco de port"
                                    placeholder="Franco de port"
                                    value={customer?.francoPort || ''}
                                    id="francoPort"
                                    type="text"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <FieldGroupLabel>
                                Bon de livraison
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email bon de livraison"
                                    placeholder="contact@toola.eu"
                                    error={'Email invalide'}
                                    isInvalid={
                                        !isValidEmail(customer?.deliveryEmail)
                                    }
                                    value={customer?.deliveryEmail || ''}
                                    id="deliveryEmail"
                                    type="email"
                                    onChange={handleChange}
                                    required={false}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3 grid">
                                <InputField
                                    label={`Notes utiles de livraison`}
                                    placeholder="Notes utiles de livraison"
                                    value={customer?.deliveryNotes}
                                    id="deliveryNotes"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <ToggleCard
                                    id="sendDeliveryEmail"
                                    label="Envoyer le BL à la livraison"
                                    checked={!!customer?.sendDeliveryEmail}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            sendDeliveryEmail:
                                                !prevCustomer.sendDeliveryEmail,
                                        }));
                                    }}
                                />
                            </div>
                            <AnimatePresence>
                                {customer?.sendDeliveryEmail && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.15 }}
                                        className="col-span-12 lg:col-span-3 z-40"
                                    >
                                        <SearchSelector
                                            label="Type de bon de livraison"
                                            displayKey="name"
                                            value={customer?.deliveryNoteType || 'with-price'}
                                            inputSmall={true}
                                            options={[
                                                {
                                                    _id: 'with-price',
                                                    name: 'Avec prix',
                                                },
                                                {
                                                    _id: 'without-price',
                                                    name: 'Sans prix',
                                                },
                                            ]}
                                            onSelect={(e: any) => {
                                                setSaveStatus('saving');
                                                setCustomer((prevCustomer: any) => ({
                                                    ...prevCustomer,
                                                    deliveryNoteType: e._id,
                                                }));
                                            }}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </Card>
                </div>

                {/* Informations de facturation */}
                <div className="col-span-12">
                    <Card extra="w-full !p-5 md:!p-6 border">
                        <SectionHeader
                            icon={Receipt}
                            title="Facturation"
                            description="Modalités, adresse, mentions légales et RIB"
                        />
                        <div className="grid gap-4 grid-cols-12">
                            <FieldGroupLabel>Facturation</FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email de facturation*"
                                    placeholder="mathieu@toola.eu"
                                    error={'Email invalide'}
                                    isInvalid={
                                        !isValidEmail(customer?.billingEmail)
                                    }
                                    value={customer?.billingEmail || ''}
                                    id="billingEmail"
                                    type="email"
                                    onChange={handleChange}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Email de facturation secondaire"
                                    placeholder="mathieu@toola.eu"
                                    error={'Email invalide'}
                                    isInvalid={
                                        !isValidEmail(customer?.billingEmail)
                                    }
                                    value={customer?.secondBillingEmail || ''}
                                    id="secondBillingEmail"
                                    type="email"
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Période de facturation*"
                                    displayKey="name"
                                    value={customer?.billingPeriod}
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'flexible',
                                            name: 'flexible',
                                        },
                                        {
                                            _id: 'delivery',
                                            name: 'à la livraison',
                                        },
                                        {
                                            _id: '7-days',
                                            name: '7 jours',
                                        },
                                        {
                                            _id: '10-days',
                                            name: '10 jours',
                                        },
                                        {
                                            _id: '15-days',
                                            name: '15 jours',
                                        },
                                        {
                                            _id: '20-days',
                                            name: '20 jours'
                                        },
                                        { _id: '1-month', name: '1 mois' },
                                    ]}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            billingPeriod: e._id,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <SearchSelector
                                    label="Moyen de paiement*"
                                    displayKey="name"
                                    value={customer?.paymentMethod}
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'cash',
                                            name: 'Espèces',
                                        },
                                        {
                                            _id: 'check',
                                            name: 'Chèque',
                                        },
                                        {
                                            _id: 'transfer',
                                            name: 'Virement',
                                        },
                                        {
                                            _id: 'credit-card',
                                            name: 'Carte bancaire',
                                        },
                                        {
                                            _id: 'direct-debit',
                                            name: 'Prélèvement',
                                        },
                                        {
                                            _id: 'LCR',
                                            name: 'LCR',
                                        },
                                    ].filter(
                                        (e) =>
                                            !user ||
                                            user?.acceptedPaymentMethods?.includes(
                                                e._id,
                                            ),
                                    )}
                                    onSelect={(e: any) => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            paymentMethod: e._id,
                                        }));
                                    }}
                                />
                            </div>

                            <FieldGroupLabel>Créance</FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label="Créance possible"
                                    placeholder="0"
                                    value={customer?.creancePossible ?? ''}
                                    id="creancePossible"
                                    type="number"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                {canBlockCustomer ? (
                                    <ToggleCard
                                        id="isBlocked"
                                        label="Client bloqué"
                                        checked={!!customer?.isBlocked}
                                        onChange={handleToggleBlocked}
                                    />
                                ) : (
                                    <div className="flex h-full items-center justify-between gap-3 rounded-xl border border-gray-200 px-4 py-3 dark:border-white/10">
                                        <span className="pr-2 text-xs font-medium text-navy-700 dark:text-white">
                                            Client bloqué
                                        </span>
                                        <span
                                            className={`h-2.5 w-2.5 rounded-full ${
                                                customer?.isBlocked
                                                    ? 'bg-red-500'
                                                    : 'bg-gray-300'
                                            }`}
                                        />
                                    </div>
                                )}
                            </div>
                            {canRestrictCustomer && (
                                <div className="col-span-12 lg:col-span-3">
                                    <ToggleCard
                                        id="restrictedToUserId"
                                        label="Client sensible (visible par moi seul)"
                                        checked={restrictedByMe}
                                        disabled={restrictActionPending || restrictedByOther}
                                        onChange={handleToggleRestricted}
                                    />
                                </div>
                            )}
                            {restrictedByOther && (
                                <div className="col-span-12 lg:col-span-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-400">
                                    Ce client est verrouillé par un autre responsable — seul lui ou le propriétaire du compte peut le déverrouiller.
                                </div>
                            )}
                            <AnimatePresence>
                                {showBlockReasonInput && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.15 }}
                                        className="col-span-12 lg:col-span-6 flex items-end gap-2"
                                    >
                                        <div className="flex-1">
                                            <InputField
                                                label="Raison du blocage"
                                                placeholder="Ex: encours dépassé, litige facture n°..."
                                                value={blockReasonDraft}
                                                id="blockReasonDraft"
                                                type="text"
                                                onChange={(e: any) =>
                                                    setBlockReasonDraft(e.target.value)
                                                }
                                                inputSmall={true}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            disabled={blockActionPending}
                                            onClick={confirmBlockCustomer}
                                            className="mb-1 shrink-0 rounded-xl bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                                        >
                                            Bloquer
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowBlockReasonInput(false);
                                                setBlockReasonDraft('');
                                            }}
                                            className="mb-1 shrink-0 rounded-xl border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 dark:border-white/10 dark:text-gray-300"
                                        >
                                            Annuler
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                            {customer?.isBlocked && (
                                <div className="col-span-12 lg:col-span-6 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-400">
                                    Bloqué le{' '}
                                    {customer.blockedAt
                                        ? new Date(customer.blockedAt).toLocaleDateString('fr-FR')
                                        : '—'}
                                    {blockedByName ? ` par ${blockedByName}` : ''}
                                    {customer.blockedReason ? ` — ${customer.blockedReason}` : ''}
                                </div>
                            )}

                            <FieldGroupLabel>
                                Adresse de facturation
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-6">
                                <InputField
                                    label={`Adresse de facturation*`}
                                    placeholder="Champ de Mars, 5 Av. Anatole France"
                                    id="billingAddress"
                                    type="text"
                                    value={customer?.billingAddress || ''}
                                    onChange={handleChange}
                                    required={true}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-3">
                                <InputField
                                    label={`Code postal`}
                                    placeholder="75007"
                                    id="billingZipCode"
                                    type="text"
                                    value={customer?.billingZipCode || ''}
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-3">
                                <InputField
                                    label={`Ville`}
                                    placeholder="Paris"
                                    id="billingCity"
                                    type="text"
                                    value={customer?.billingCity || ''}
                                    onChange={handleChange}
                                    isLoaded={customer}
                                    inputSmall={true}
                                />
                            </div>

                            <FieldGroupLabel>
                                Mentions légales
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Numéro de SIREN`}
                                    placeholder="123568941"
                                    value={customer?.siren}
                                    id="siren"
                                    type="text"
                                    onChange={handleChange}
                                    //isInvalid={!isValidSiren(customer?.siren)}
                                    error={'SIREN invalide'}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`Numéro de TVA`}
                                    placeholder="FR32123456789"
                                    value={customer?.vatNumber}
                                    id="vatNumber"
                                    type="text"
                                    onChange={handleChange}
                                    // isInvalid={
                                    //     !isValidVATNumber(customer?.vatNumber)
                                    // }
                                    error={'Numéro de TVA invalide'}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <SearchSelector
                                    label="Échéances des factures"
                                    displayKey="name"
                                    value={
                                        customer?.invoiceDueDelay ||
                                        user?.invoiceDueDelay
                                    }
                                    inputSmall={true}
                                    options={[
                                        {
                                            _id: 'direct',
                                            name: 'À réception',
                                        },
                                        {
                                            _id: '7-days',
                                            name: '7 jours',
                                        },
                                        {
                                            _id: '10-days',
                                            name: '10 jours',
                                        },
                                        {
                                            _id: '15-days',
                                            name: '15 jours',
                                        },
                                        {
                                            _id: '20-days',
                                            name: '20 jours',
                                        },
                                        {
                                            _id: '30-days',
                                            name: '30 jours',
                                        },
                                        {
                                            _id: 'end-of-month',
                                            name: 'Fin du mois',
                                        },
                                        {
                                            _id: '30-days-end-of-month',
                                            name: '30 jours fin de mois',
                                        },
                                        {
                                            _id: '45-days',
                                            name: '45 jours',
                                        },
                                        {
                                            _id: '60-days',
                                            name: '60 jours',
                                        },
                                        {
                                            _id: '90-days',
                                            name: '90 jours',
                                        },
                                        {
                                            _id: '20pct-deposit-80pct-30days',
                                            name: '20% acompte + 80% à 30 jours',
                                        },
                                        {
                                            _id: '50pct-deposit-50pct-30days',
                                            name: '50% acompte + 50% à 30 jours',
                                        },
                                        CUSTOM_SCHEDULE_OPTION,
                                    ]}
                                    onSelect={(e: any) => {
                                        const decoded = decodeLegacyDepositPreset(e._id);
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            invoiceDueDelay: e._id,
                                            paymentSchedule:
                                                e._id === CUSTOM_SCHEDULE_OPTION._id
                                                    ? prevCustomer.paymentSchedule?.length
                                                        ? prevCustomer.paymentSchedule
                                                        : decodeLegacyDepositPreset(
                                                              prevCustomer.invoiceDueDelay,
                                                          ) || DEFAULT_CUSTOM_SCHEDULE
                                                    : decoded || [],
                                        }));
                                    }}
                                />
                                {customer?.invoiceDueDelay === CUSTOM_SCHEDULE_OPTION._id && (
                                    <div className="mt-4 rounded-xl border border-gray-100 p-3 dark:border-white/10">
                                        <PaymentScheduleEditor
                                            value={customer?.paymentSchedule || []}
                                            onChange={(rows) =>
                                                setCustomer((prevCustomer: any) => ({
                                                    ...prevCustomer,
                                                    paymentSchedule: rows,
                                                }))
                                            }
                                        />
                                    </div>
                                )}
                            </div>

                            <FieldGroupLabel>
                                RIB & prélèvement SEPA
                            </FieldGroupLabel>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`RIB personnalisé pour ce client`}
                                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                                    id="customerRib"
                                    type="text"
                                    onChange={handleChange}
                                    error="RIB invalide"
                                    value={customer?.customerRib}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label={`RIB du client`}
                                    placeholder="FR76 1234 5678 9012 3456 7890 123"
                                    id="herRib"
                                    type="text"
                                    onChange={handleChange}
                                    value={customer?.herRib}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <ToggleCard
                                    id="useSecondRib"
                                    label="Utiliser le RIB particulier"
                                    checked={!!customer?.useSecondRib}
                                    onChange={() => {
                                        setSaveStatus('saving');
                                        setCustomer((prevCustomer: any) => ({
                                            ...prevCustomer,
                                            useSecondRib:
                                                !prevCustomer.useSecondRib,
                                        }));
                                    }}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label={`Référence du mandat SEPA (RUM)`}
                                    placeholder="RUM-00001"
                                    id="mandateReference"
                                    type="text"
                                    onChange={handleChange}
                                    value={customer?.mandateReference}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-3">
                                <InputField
                                    label={`Date de signature du mandat`}
                                    id="mandateSignatureDate"
                                    type="date"
                                    onChange={handleChange}
                                    value={customer?.mandateSignatureDate ? String(customer.mandateSignatureDate).slice(0, 10) : ''}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-6">
                                <InputField
                                    label={`Message sur facture`}
                                    placeholder="Merci de votre confiance"
                                    id="invoiceMessage"
                                    type="text"
                                    onChange={handleChange}
                                    value={customer?.invoiceMessage || ''}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Banque */}
                <div className="col-span-12">
                    <Card extra="w-full !p-5 md:!p-6 border">
                        <SectionHeader
                            icon={Landmark}
                            title="Banque"
                            description="Coordonnées bancaires du client"
                        />
                        <div className="grid gap-4 grid-cols-12">
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="Nom de la banque"
                                    placeholder="BNP Paribas"
                                    value={customer?.bankName || ''}
                                    id="bankName"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Code banque"
                                    placeholder="30004"
                                    value={customer?.bankCode || ''}
                                    id="bankCode"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Code guichet"
                                    placeholder="00825"
                                    value={customer?.bankBranchCode || ''}
                                    id="bankBranchCode"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-12 lg:col-span-4">
                                <InputField
                                    label="N° de compte"
                                    placeholder="00001234567"
                                    value={customer?.bankAccountNumber || ''}
                                    id="bankAccountNumber"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-2">
                                <InputField
                                    label="Clé RIB"
                                    placeholder="89"
                                    value={customer?.bankRibKey || ''}
                                    id="bankRibKey"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-6 lg:col-span-4">
                                <InputField
                                    label="BIC"
                                    placeholder="BNPAFRPPXXX"
                                    value={customer?.bankBic || ''}
                                    id="bankBic"
                                    type="text"
                                    onChange={handleChange}
                                    inputSmall={true}
                                />
                            </div>
                        </div>
                    </Card>
                </div>

                {saveStatus === 'saved' && (
                    <div className="col-span-12 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-green-100 bg-green-50 px-5 py-3 text-sm text-green-700 dark:border-green-400/20 dark:bg-green-400/10 dark:text-green-400">
                        <span className="flex items-center gap-2">
                            <CheckCircle2 size={16} />
                            Modifications enregistrées automatiquement
                        </span>
                        <GoBack text="Retour à la fiche client" />
                    </div>
                )}
            </div>
        </>
    );
};

export default EditInformationCustomer;
